import { instance } from '@app/api/axios';
import { useShowToaster } from '@shared/ui/messages/useToaster';
import { useRequestError } from '@app/api/useRequestError';
import { useDispatch, useSelector } from 'react-redux';
import { setMyUsersParams } from '@app/store/slices/myUsersSlice';
import { setMyTeamParams, fetchMyTeam } from '@app/store/slices/myTeamSlice';
import { useNavigate } from 'react-router-dom';
import { fetchGroupById } from '@app/store/slices/groupIdSlice';

const REFETCH_DELAY_MS = 1500;

export function useGroups() {
  const showToaster = useShowToaster();
  const handleRequestError = useRequestError();
  const dispatch = useDispatch();
  const { activeTab } = useSelector((state) => state.my_team);
  const { group } = useSelector((state) => state.group_id);
  const navigate = useNavigate();

  const refreshGroup = () => {
    if (group?.id) {
      dispatch(fetchGroupById());
    } else {
      dispatch(fetchMyTeam());
    }
  };

  const createGroup = async (data) => {
    try {
      const res = await instance.post('/create_group', data);
      showToaster('success', res?.data?.message);
      const setParams =
        activeTab === 'teams' ? setMyTeamParams : setMyUsersParams;
      dispatch(setParams({ page: 1, sort_by: 'created_at', sort_dir: 'desc' }));
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  const deleteGroup = async (data) => {
    try {
      const res = await instance.post('/group_delete', data);
      showToaster('success', res?.data?.message);
      setTimeout(() => {
        if (group?.id) {
          navigate('/teams');
        } else {
          dispatch(fetchMyTeam());
        }
      }, REFETCH_DELAY_MS);
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const fetchAvailableTeamLeads = async () => {
    try {
      const res = await instance.post('/get_leads_groups_count');
      return res?.data;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const fetchGroupsWithoutTeamLead = async () => {
    try {
      const res = await instance.post('/group_without_lead');
      return res?.data?.groups;
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const unfreezeGroup = async (data) => {
    try {
      const res = await instance.post('/unfreeze_group', data);
      showToaster('success', res?.data?.message);
      refreshGroup();
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const freezeGroup = async (data) => {
    try {
      const res = await instance.post('/freeze_group', data);
      showToaster('success', res?.data?.message);
      refreshGroup();
    } catch (err) {
      showToaster('error', err?.response?.data?.message);
    }
  };

  const updateGroup = async (data, oldValue) => {
    try {
      const res = await instance.post('/update_group', data);
      showToaster('success', res?.data?.message);
      if (group?.id) {
        const newSlug = res?.data?.data?.[0]?.slug;
        if (data.group?.name && oldValue?.name !== data.group.name && newSlug) {
          navigate(`/teams/${newSlug}`);
        } else {
          dispatch(fetchGroupById());
        }
      } else {
        dispatch(fetchMyTeam());
      }
      return true;
    } catch (err) {
      return handleRequestError(err);
    }
  };

  return {
    createGroup,
    deleteGroup,
    fetchAvailableTeamLeads,
    fetchGroupsWithoutTeamLead,
    unfreezeGroup,
    freezeGroup,
    updateGroup,
  };
}
